import { useEffect, useState } from "react";
import {  useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Card, Form, Message, Modal, Spin, Tooltip } from "@arco-design/web-react";
import { IconLeft } from "@arco-design/web-react/icon";
import axios from "axios";
import classNames from "classnames";

import { ReactComponent as EditIcon } from "../../../assets/icons/edit.svg";
import { ReactComponent as TrashIcon } from "../../../assets/icons/trash.svg";
import { ReactComponent as KeyIcon } from "../../../assets/icons/key.svg";
import { ReactComponent as MailIcon } from "../../../assets/icons/mail.svg";
import { endpoints } from "../../../api/endpoints";
import { UserCard } from "../../../components/UserCard/UserCard";
import { UserDetailedInfo } from "../../../components/UserDetailedInfo/UserDetailedInfo";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";

import { User, Role } from "../../../models/Person";
import { routes } from "../../../routes/routes";
import { setSelectedUser } from "../../../store/adminSlice";
import { RootState } from "../../../store/store";
import { UserForm } from "../UserForm/UserForm";

import "./UserProfile.css";
import { currentThemeName } from "../../../theme";

export function UserProfile() {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const currentUser = useSelector((state: RootState) => state.user.user);
  const selectedUser = useSelector((state: RootState) => state.admin.selectedUser);
  const hasUserData = selectedUser?.id.toString() === id;

  const [isLoading, setIsLoading] = useState<boolean>(!hasUserData);
  const [user, setUser] = useState<User | null>(hasUserData ? selectedUser : null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showEditConfirmation, setShowEditConfirmation] = useState<boolean>(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [showPasswordResetConfirmation, setShowPasswordResetConfirmation] = useState<boolean>(false);
  const [showResendConfirmationDialog, setShowResendConfirmationDialog] = useState<boolean>(false);

  const getProfile = async () => {
    axios
      .get(`${endpoints.admin.getUser}/${id}`)
      .then((response) => {
        setUser(response.data);
        dispatch(
          setSelectedUser(response.data)
        )
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  useEffect(() => {
    getProfile().then();
  }, []);


  const handleSubmitEdit = () => {
    if (form) {
      const fields = form.getFields();
      const formData = new FormData();

      formData.append("isAdmin", String(Boolean(user?.isAdmin)));
      formData.append("isMentor", String(Boolean(user?.isMentor)));
      formData.append("isGuest", String(Boolean(user?.isGuest)));

      Object.keys(fields).forEach((key) => {
        if (Array.isArray(fields[key])) {
          fields[key].forEach((value: string) => {
            formData.append(key, value);
          });
        } else {
          formData.append(key, fields[key]);
        }
      });

      form.validate().then(() => {
        axios
          .put(`${endpoints.admin.createUser}/${user?.id}`, formData)
          .then((response) => {
            setUser(response.data);
            setIsEditing(false);
            setShowEditConfirmation(false);
            dispatch(
              setSelectedUser(response.data)
            );
            Message.success({
              content: t('notification_messages.profile_updated_success'),
              closable: true,
              duration: 3000,
            });
          })
          .catch((error) => {
            console.error(error);
            setShowEditConfirmation(false);
            Message.error(t('notification_messages.profile_updated_error'));
          })
          .finally(() => {
            form.resetFields();
          });
      });
    }
  };

  const handleDelete = () => {
    axios
      .delete(`${endpoints.admin.createUser}/${user?.id}`)
      .then(() => {
        dispatch(
          setSelectedUser(null)
        )
        Message.success({content: t('admin.user_profile.user_deleted_success'), closable: true, duration: 3000});
        navigate(routes.ADMIN_USERS)
      })
      .catch((error) => {
        console.error(error);
        Message.error(t('admin.user_profile.user_deleted_error'));
      })
  };

  const handlePasswordReset = () => {
    if (user?.email) {
      axios
        .post(endpoints.admin.resetPassword, {
          "email": user.email,
        })
        .then(() => {
          Message.success({content: t('admin.user_profile.reset_sent_success'), closable: true, duration: 3000});
        })
        .catch((error) => {
          console.error(error);
          Message.error(t('admin.user_profile.reset_sent_error'));
        })
    } else {
      Message.error(t('admin.user_profile.reset_error'));
    }
  };

  const handleResendConfirmationEmail = () => {
    if (!user?.email) {
      Message.error(t('admin.user_profile.resend_confirmation_error'));
      return;
    }

    axios
      .post(endpoints.confirmEmail, {
        email: user.email
      })
      .then(() => {
        Message.success({content: t('admin.user_profile.resend_confirmation_success'), closable: true, duration: 3000});
      })
      .catch((error) => {
        console.error(error);
        Message.error(t('admin.user_profile.resend_confirmation_error'));
      });
  };

  const cleanEmptyValues = (userData: User | null) => {
    if (!userData) return null;
    
    // Convert current_industry string to array if it exists
    let currentIndustryArray: string[] | undefined = undefined;
    if (userData.current_industry) {
      const currentIndustryValue = userData.current_industry as string[] | string;
      if (typeof currentIndustryValue === 'string') {
        currentIndustryArray = currentIndustryValue.includes(';;;')
          ? currentIndustryValue.split(';;;').filter((s: string) => s.trim())
          : [currentIndustryValue];
      } else if (Array.isArray(currentIndustryValue)) {
        currentIndustryArray = currentIndustryValue;
      }
    }
    
    return {
      ...userData,
      current_industry: currentIndustryArray,
      borough: userData.borough || undefined,
      gender: userData.gender || undefined,
      age: userData.age || undefined,
      race: userData.race || undefined,
      ethnicity: userData.ethnicity || undefined,
      education: userData.education || undefined,
      incomeRange: userData.incomeRange || undefined,
      occupationStatus: userData.occupationStatus || undefined,
      professionalExperience: userData.professionalExperience || undefined,
      howYouFoundUs: userData.howYouFoundUs || undefined,
      business_start_period: userData.business_start_period || undefined,
      mwbe: userData.mwbe || undefined,
      employees_count: userData.employees_count || undefined,
      business_stage: userData.business_stage || undefined,
      important_goal: userData.important_goal || undefined,
      business_location: userData.business_location || undefined,
      revenue: userData.revenue || undefined,
      item_types: userData.item_types || undefined,
      areaInterest: userData.areaInterest || undefined
    };
  };

  const handleRevokeAdmin = () => {
    if (!user) {
      return;
    }
    Modal.confirm({
      title: t('admin.users_list.remove_admin_confirm_title'),
      content: t('admin.users_list.remove_admin_confirm_body', { name: user.name }),
      okText: t('common.ok_text'),
      cancelText: t('common.cancel_button'),
      okButtonProps: { status: 'danger' },
      onOk: async () => {
        try {
          await axios.delete(`${endpoints.admin.revokeAdmin}/${user.id}`);
          Message.success(t('admin.users_list.remove_admin_success'));
          navigate(routes.ADMIN_USERS);
        } catch (error) {
          console.error(error);
          Message.error(t('admin.users_list.remove_admin_error'));
        }
      },
    });
  };

  const userRole: Role = user?.isAdmin ? 'admin' : user?.isMentor ? 'mentor' : user?.isGuest ? 'guest' : 'mentee';
  const isSuperAdmin = Boolean(currentUser?.isSuperAdmin);

  const publicProfileUrl = user && !user.isAdmin
    ? user.isMentor
      ? routes.MENTOR.replace(':id', String(user.id))
      : routes.MENTEE.replace(':id', String(user.id))
    : undefined;

  return (
    <div className={classNames("user-profile", {
      "user-profile--black-theme": currentThemeName === "black"
    })}>
      <div className="user-profile__header">
        <div className="user-profile__header-left">
          <Button
            onClick={() => {
              navigate(routes.ADMIN_USERS);
            }}
            shape="round"
            className="user-profile__icon-button"
          >
            <IconLeft fontSize={24}/>
          </Button>
          <h1 className="user-profile__title">{t('admin.user_profile.title')}</h1>
        </div>
        <div className="user-profile__header-actions">
          <div className="user-profile__action-buttons">
            <Tooltip content={t('admin.user_profile.edit_profile_tooltip')}>
              <Button
                className={classNames('user-profile__icon-button', { 'user-profile__icon-button--active': isEditing })}
                shape={"round"}
                onClick={() => {
                  form.setFieldsValue(cleanEmptyValues(user));
                  setIsEditing(true);
                }}
              >
                <EditIcon />
              </Button>
            </Tooltip>
            {selectedUser?.id !== currentUser?.id && (
              <Tooltip content={t('admin.user_profile.delete_profile_tooltip')}>
                <Button
                  className="user-profile__icon-button"
                  shape={"round"}
                  onClick={() => setShowDeleteConfirmation(true)}
                >
                  <TrashIcon />
                </Button>
              </Tooltip>
            )}
            <Tooltip content={t('admin.user_profile.reset_password_tooltip')}>
              <Button
                className="user-profile__icon-button"
                shape={"round"}
                onClick={() => setShowPasswordResetConfirmation(true)}
              >
                <KeyIcon />
              </Button>
            </Tooltip>
            <Tooltip content={t('admin.user_profile.resend_confirmation_tooltip')}>
              <Button
                className="user-profile__icon-button"
                shape={"round"}
                onClick={() => setShowResendConfirmationDialog(true)}
              >
                <MailIcon />
              </Button>
            </Tooltip>
          </div>
          {user?.isAdmin && isSuperAdmin && user.id !== currentUser?.id && (
            <Button
              className="user-profile__revoke-admin-button"
              shape="round"
              size="small"
              onClick={handleRevokeAdmin}
            >
              {t('admin.users_list.remove_admin')}
            </Button>
          )}
        </div>
      </div>

      {!user && isLoading && (
        <div className="user-profile__loader-wrapper">
          <Spin size={40}/>
        </div>
      )}
      {!isLoading && user && (
        <div className="user-profile__content">
          {!isEditing && (
            <>
              <UserCard
                user={user}
                roleTag={
                  user.isAdmin
                    ? 'ADMIN'
                    : user.isMentor
                      ? 'MENTOR'
                      : user.isGuest
                        ? 'GUEST'
                        : 'MENTEE'
                }
                hideBooking={Boolean(user.isAdmin) || !user.isMentor}
                toProfileUrl={publicProfileUrl}
                profilePreview={
                  publicProfileUrl
                    ? {
                        avatar_url: user.avatar_url,
                        name: user.name,
                        last_name: user.last_name,
                        shortDescription: user.shortDescription,
                      }
                    : undefined
                }
              />
              <Card className="user-profile__card" bordered={false}>
                <div className="user-profile__card-header">{t('admin.user_profile.detailed_information')}</div>
                <UserDetailedInfo user={user}/>
              </Card>
              {!user.isAdmin && (
                <Card className="user-profile__card" bordered={false}>
                  <div className="user-profile__card-header">{t('admin.user_profile.about_me')}</div>
                  <div className="user-profile__card-text-content" style={{ whiteSpace: 'pre-line' }}>{user.fullBio || '-'}</div>
                </Card>
              )}
            </>
          )}

          {isEditing && (
            <UserForm
              role={userRole}
              form={form}
              isEditing
              avatarUrl={user.avatar_url}
              onSubmit={() => setShowEditConfirmation(true)}
              onCancel={() => {
                form.resetFields();
                setIsEditing(false);
              }}
            />
          )}
          <ConfirmationDialog
            visible={showEditConfirmation}
            text={t('admin.user_profile.save_changes_confirmation')}
            onConfirm={() => {
              handleSubmitEdit();
            }}
            onCancel={() => {
              setShowEditConfirmation(false);
            }}
          />
          <ConfirmationDialog
            visible={showDeleteConfirmation}
            text={t('admin.user_profile.delete_profile_confirmation')}
            onConfirm={() => {
              handleDelete();
              setShowDeleteConfirmation(false);
            }}
            onCancel={() => {
              setShowDeleteConfirmation(false);
            }}
          />
          <ConfirmationDialog
            visible={showPasswordResetConfirmation}
            text={t('admin.user_profile.reset_password_confirmation')}
            onConfirm={() => {
              handlePasswordReset();
              setShowPasswordResetConfirmation(false);
            }}
            onCancel={() => {
              setShowPasswordResetConfirmation(false);
            }}
          />
          <ConfirmationDialog
            visible={showResendConfirmationDialog}
            text={t('admin.user_profile.resend_confirmation_confirmation')}
            onConfirm={() => {
              handleResendConfirmationEmail();
              setShowResendConfirmationDialog(false);
            }}
            onCancel={() => {
              setShowResendConfirmationDialog(false);
            }}
          />
        </div>
      )}
    </div>
  );
}